// Search-list filter for the Metrics screen (docs: the "search list"
// design, concept A). One control for every filter field: a search-box
// button that opens a popover with a server-side searched list of values,
// each with its count, checkboxes to pick or leave out values, and an
// "All containing ..." toggle that keeps the typed words as a term.
//
// ---- Field state -------------------------------------------------------
//
//   { terms: ['water burst'], picks: ['tensile test'], excludes: ['...'] }
//
//   Match = (any term OR any pick) AND NOT any exclude. Empty terms and
//   picks = no filter (excludes still apply: "everything except").
//   A term matches values containing every word, any order, case-
//   insensitive (server: Metrics::TermMatch). Terms are stored normalized
//   (trimmed, lower case, single spaces) WITHOUT the "~" prefix; the URL /
//   param helpers below add it (task[]=~water burst).
//
// ---- Creating one ------------------------------------------------------
//
//   const list = createSearchList(anchorEl, {
//     label: 'Task',                  // chip key and aria label
//     placeholder: 'All tasks',       // closed text with no filter
//     searchPlaceholder: 'Search tasks',
//     nounOne: 'name', nounMany: 'names',   // the values
//     countNoun: 'results',           // what n counts
//     supportsTerms: true,            // show the "All containing" toggle
//     supportsExcludes: true,         // false: a term's rows stay checked
//                                     // (no "leave out"; job chips)
//     outOfRangeText: 'no results in range',
//     state: { terms, picks, excludes },
//     labels: new Map(),              // optional shared value -> label map
//     termTotals: new Map(),          // optional shared term -> name count
//     fetchOptions: (q, state) => Promise<{ options: [{ value, label, n,
//       in_range, pinned }], total_options, total_count }>,
//         // the field's options endpoint (list=1). Send the state's picks
//         // and excludes as selected[] so they always come back.
//     onApply: state => {},           // Apply, or a chip's remove button
//     onLabels: () => {}              // optional: labels arrived (ensureLabels)
//   })
//
//   anchorEl is emptied and gets the closed button; the popover is
//   appended inside it (position: fixed, so no ancestor clips it).
//
// ---- Instance ----------------------------------------------------------
//
//   list.getState()          copy of the applied state
//   list.setState(state)     replace it (no onApply), re-render the button
//   list.apply(state)        setState + onApply (what Apply and chips do)
//   list.open() / close() / isOpen()
//   list.summary()           closed-button text ("water burst · 23 of 24")
//   list.chips()             chip descriptors for renderFilterChips()
//   list.labelFor(value)     known label, else the value
//   list.ensureLabels()      fetch labels for picks/excludes that have none
//                            (restored saved views); calls onLabels
//   list.ensureCounts()      fetch each term's name count ("23 of 24") for
//                            a restored view; calls onLabels
//   list.forgetCounts()      drop cached per-term name counts (range change)
//   list.setDisabled(bool, text)
//   list.destroy()           Stimulus disconnect(): removes listeners/DOM
//
// ---- Helpers -----------------------------------------------------------
//
//   emptyState(), normalizeTerm(q), termWords(term), matchesTerm(text, term)
//   stateFromTokens(tokens, excludes)  task[]-style tokens ('~x' = term)
//   stateToTokens(state)              -> ['~x', 'pick', ...]
//   isEmptyState(state)
//   summarize(state, opts)            same text as list.summary()
//   fieldChips(state, { label, nounOne, nounMany, labelFor, termTotals,
//              onChange(state), onOpen() })  chips for a field with no
//                                     instance (e.g. a trait not shown)
//   renderFilterChips(host, chips)    appends chips (does not clear host)
//     chips: [{ key, value, meta, title, negative, onRemove, onOpen }]
//
// Markup/CSS: .m-sl* and .m-chip-neg/.m-chip-open in metrics.scss
// ("Search-list filters").

export const CONTAINS_PREFIX = '~'
const MAX_LISTED_EXCLUDES = 2

export function emptyState() {
  return { terms: [], picks: [], excludes: [] }
}

export function normalizeTerm(q) {
  return String(q || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

export function termWords(term) {
  return [...new Set(normalizeTerm(term).split(' ').filter(Boolean))]
}

export function matchesTerm(text, term) {
  const words = termWords(term)
  if (!words.length) return false
  const hay = String(text || '').toLowerCase()
  return words.every(w => hay.includes(w))
}

function uniq(list) {
  return [...new Set(list.filter(v => v !== null && v !== undefined && v !== ''))]
}

function cleanState(state) {
  const s = state || {}
  return {
    terms: uniq((s.terms || []).map(normalizeTerm)),
    picks: uniq((s.picks || []).map(String)),
    excludes: uniq((s.excludes || []).map(String))
  }
}

export function isEmptyState(state) {
  const s = cleanState(state)
  return !s.terms.length && !s.picks.length && !s.excludes.length
}

// task[]-style tokens: '~words' is a term, anything else an exact pick.
export function stateFromTokens(tokens, excludes = []) {
  const state = emptyState()
  const list = tokens || []
  list.forEach((raw) => {
    const t = String(raw || '')
    if (t.startsWith(CONTAINS_PREFIX)) state.terms.push(t.slice(CONTAINS_PREFIX.length))
    else state.picks.push(t)
  })
  state.excludes = excludes || []
  return cleanState(state)
}

export function stateToTokens(state) {
  const s = cleanState(state)
  return s.terms.map(t => `${CONTAINS_PREFIX}${t}`).concat(s.picks)
}

function fmt(n) {
  return Number(n || 0).toLocaleString('en-US')
}

function plural(n, one, many) {
  return n === 1 ? one : many
}

// Excludes a term covers, for "23 of 24"
function excludedUnder(state, term, labelFor) {
  return state.excludes.filter(v => matchesTerm(v, term) || matchesTerm(labelFor(v), term)).length
}

function termMeta(state, term, opts) {
  const total = opts.termTotals ? opts.termTotals.get(term) : undefined
  if (total === undefined) return ''
  const out = excludedUnder(state, term, opts.labelFor)
  return out ? `${fmt(Math.max(0, total - out))} of ${fmt(total)}` : `${fmt(total)} ${plural(total, opts.nounOne, opts.nounMany)}`
}

function picksText(picks, opts) {
  if (!picks.length) return ''
  if (picks.length <= 2) return picks.map(opts.labelFor).join(' or ')
  return `${picks.length} ${opts.nounMany}`
}

export function summarize(state, opts) {
  const o = {
    labelFor: v => v, nounOne: 'value', nounMany: 'values', placeholder: 'All', ...opts
  }
  const s = cleanState(state)
  const parts = s.terms.map((t) => {
    const meta = termMeta(s, t, o)
    return meta ? `${t} · ${meta}` : t
  })
  if (s.picks.length) parts.push(picksText(s.picks, o))
  if (!parts.length) {
    return s.excludes.length ? `All but ${s.excludes.length} ${plural(s.excludes.length, o.nounOne, o.nounMany)}` : o.placeholder
  }
  let text = parts.join(' + ')
  const covered = s.terms.some(t => termMeta(s, t, o) !== '')
  if (s.excludes.length && !covered) text += ` · ${s.excludes.length} left out`
  return text
}

// Chip descriptors for a field's state (renderFilterChips): one chip per
// term, one for the picks, one red "not ..." chip per exclusion (merged
// into "not N names" past two). opts: label, nounOne, nounMany,
// labelFor, termTotals (Map term -> names matching, for "23 of 24"),
// onChange(newState) for the remove buttons, onOpen() for the chip body.
export function fieldChips(state, opts) {
  const o = {
    labelFor: v => v, nounOne: 'value', nounMany: 'values', label: '', ...opts
  }
  const s = cleanState(state)
  const change = next => o.onChange(cleanState(next))
  const out = []
  s.terms.forEach((t) => {
    out.push({
      key: `${o.label} contains`,
      value: t,
      meta: termMeta(s, t, o),
      onOpen: o.onOpen,
      removeTitle: `Remove "${t}"`,
      onRemove: () => change({ ...s, terms: s.terms.filter(x => x !== t) })
    })
  })
  if (s.picks.length) {
    out.push({
      key: `${o.label}:`,
      value: picksText(s.picks, o),
      title: s.picks.length > 2 ? s.picks.map(o.labelFor).join('\n') : '',
      onOpen: o.onOpen,
      removeTitle: `Remove the picked ${o.nounMany}`,
      onRemove: () => change({ ...s, picks: [] })
    })
  }
  if (s.excludes.length > MAX_LISTED_EXCLUDES) {
    out.push({
      value: `not ${s.excludes.length} ${o.nounMany}`,
      title: `${o.label}, left out:\n${s.excludes.map(o.labelFor).join('\n')}`,
      negative: true,
      onOpen: o.onOpen,
      removeTitle: `Include every ${o.nounOne} again`,
      onRemove: () => change({ ...s, excludes: [] })
    })
  } else {
    s.excludes.forEach((v) => {
      out.push({
        value: `not ${o.labelFor(v)}`,
        title: `${o.label}: left out`,
        negative: true,
        onOpen: o.onOpen,
        removeTitle: 'Include it again',
        onRemove: () => change({ ...s, excludes: s.excludes.filter(x => x !== v) })
      })
    })
  }
  return out
}

export function renderFilterChips(host, chips) {
  chips.forEach((c) => {
    const chip = document.createElement('span')
    chip.className = c.negative ? 'm-chip m-chip-neg' : 'm-chip'
    if (c.title) chip.title = c.title
    const body = document.createElement(c.onOpen ? 'button' : 'span')
    if (c.onOpen) {
      body.type = 'button'
      body.className = 'm-chip-open'
      body.addEventListener('click', () => c.onOpen())
    }
    if (c.key) {
      const k = document.createElement('span')
      k.className = 'm-chip-k'
      k.textContent = `${c.key} `
      body.appendChild(k)
    }
    const strong = document.createElement('strong')
    strong.textContent = c.value
    body.appendChild(strong)
    if (c.meta) {
      const meta = document.createElement('span')
      meta.className = 'm-chip-k'
      meta.textContent = ` · ${c.meta}`
      body.appendChild(meta)
    }
    chip.appendChild(body)
    if (c.onRemove) {
      const x = document.createElement('button')
      x.type = 'button'
      x.textContent = '✕'
      x.title = c.removeTitle || 'Remove'
      x.addEventListener('click', () => c.onRemove())
      chip.appendChild(x)
    }
    host.appendChild(chip)
  })
}

// Label with the words wrapped in <mark>, built as DOM (no innerHTML).
function highlighted(label, words) {
  const frag = document.createDocumentFragment()
  const text = String(label)
  const lower = text.toLowerCase()
  const marks = new Array(text.length).fill(false)
  words.forEach((w) => {
    let i = lower.indexOf(w)
    while (w && i !== -1) {
      for (let k = i; k < i + w.length; k += 1) marks[k] = true
      i = lower.indexOf(w, i + w.length)
    }
  })
  let i = 0
  while (i < text.length) {
    const on = marks[i]
    let j = i
    while (j < text.length && marks[j] === on) j += 1
    const piece = text.slice(i, j)
    if (on) {
      const m = document.createElement('mark')
      m.textContent = piece
      frag.appendChild(m)
    } else {
      frag.appendChild(document.createTextNode(piece))
    }
    i = j
  }
  return frag
}

function el(tag, className, text) {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

class SearchList {
  constructor(anchorEl, options) {
    this.anchor = anchorEl
    this.opts = {
      label: 'Filter',
      placeholder: 'All',
      searchPlaceholder: 'Search',
      nounOne: 'value',
      nounMany: 'values',
      countNoun: 'results',
      supportsTerms: true,
      supportsExcludes: true,
      outOfRangeText: 'no results in range',
      onApply: () => {},
      onLabels: () => {},
      ...options
    }
    this.labels = this.opts.labels || new Map()
    this.state = cleanState(this.opts.state)
    this.termTotals = this.opts.termTotals || new Map()
    this.seq = 0
    this.build()
    this.renderButton()
  }

  // ---- public API ----

  getState() {
    return cleanState(this.state)
  }

  setState(state) {
    this.state = cleanState(state)
    this.renderButton()
  }

  apply(state) {
    this.setState(state)
    this.opts.onApply(this.getState())
  }

  labelFor(value) {
    return this.labels.get(value) || value
  }

  summary() {
    return summarize(this.state, this.summaryOpts())
  }

  forgetCounts() {
    this.termTotals.clear()
    this.renderButton()
  }

  setDisabled(disabled, text) {
    this.disabled = !!disabled
    this.disabledText = text
    this.btn.disabled = this.disabled
    if (this.disabled) this.close()
    this.renderButton()
  }

  isOpen() {
    return !this.pop.hidden
  }

  chips() {
    return fieldChips(this.state, {
      ...this.summaryOpts(),
      label: this.opts.label,
      onChange: state => this.apply(state),
      onOpen: () => this.open()
    })
  }

  ensureLabels() {
    const missing = this.state.picks.concat(this.state.excludes).filter(v => !this.labels.has(v))
    if (!missing.length) return Promise.resolve(false)
    return this.opts.fetchOptions('', this.getState()).then((data) => {
      this.remember(data)
      this.renderButton()
      this.opts.onLabels()
      return true
    }).catch(() => false)
  }

  // "23 of 24" needs each term's name count, known once its list was
  // fetched; restored views fetch them here (cheap: the server caches the
  // grouped rows per range, q only filters them).
  ensureCounts() {
    const missing = this.state.terms.filter(t => !this.termTotals.has(t))
    if (!missing.length || !this.opts.supportsTerms) return Promise.resolve(false)
    return Promise.all(missing.map(t => this.opts.fetchOptions(t, this.getState()).then((data) => {
      this.remember(data)
      this.termTotals.set(t, data.total_options || 0)
    }))).then(() => {
      this.renderButton()
      this.opts.onLabels()
      return true
    }).catch(() => false)
  }

  open() {
    if (this.disabled || this.isOpen()) return
    const s = this.getState()
    this.draft = {
      q: s.terms[0] || '',
      termOn: s.terms.length > 0,
      otherTerms: s.terms.slice(1),
      picks: new Set(s.picks),
      excludes: new Set(s.excludes)
    }
    this.data = null
    this.input.value = this.draft.q
    this.pop.hidden = false
    this.btn.setAttribute('aria-expanded', 'true')
    this.position()
    document.addEventListener('mousedown', this.onDocDown, true)
    window.addEventListener('resize', this.onReposition)
    window.addEventListener('scroll', this.onReposition, true)
    this.renderDraft()
    this.fetch()
    this.input.focus()
    this.input.select()
  }

  close({ focus = false } = {}) {
    if (!this.pop || this.pop.hidden) return
    this.pop.hidden = true
    this.btn.setAttribute('aria-expanded', 'false')
    clearTimeout(this.timer)
    document.removeEventListener('mousedown', this.onDocDown, true)
    window.removeEventListener('resize', this.onReposition)
    window.removeEventListener('scroll', this.onReposition, true)
    if (focus) this.btn.focus()
  }

  destroy() {
    this.close()
    this.seq += 1
    this.anchor.replaceChildren()
  }

  // ---- internals ----

  summaryOpts() {
    return {
      labelFor: v => this.labelFor(v),
      nounOne: this.opts.nounOne,
      nounMany: this.opts.nounMany,
      placeholder: this.opts.placeholder,
      termTotals: this.termTotals
    }
  }

  build() {
    this.anchor.replaceChildren()
    this.anchor.classList.add('m-sl')
    this.onDocDown = (ev) => {
      if (!this.anchor.contains(ev.target)) this.close()
    }
    this.onReposition = () => this.position()

    const btn = el('button', 'm-sl-btn')
    btn.type = 'button'
    btn.setAttribute('aria-haspopup', 'dialog')
    btn.setAttribute('aria-expanded', 'false')
    btn.setAttribute('aria-label', this.opts.label)
    btn.appendChild(el('span', 'm-sl-ic', '⌕'))
    this.btnText = el('span', 'm-sl-text')
    btn.appendChild(this.btnText)
    btn.appendChild(el('span', 'm-sl-caret', '▾'))
    btn.addEventListener('click', () => (this.isOpen() ? this.close() : this.open()))
    this.btn = btn

    const pop = el('div', 'm-sl-pop')
    pop.hidden = true
    pop.setAttribute('role', 'dialog')
    pop.setAttribute('aria-label', this.opts.label)

    const search = el('div', 'm-sl-search')
    search.appendChild(el('span', 'm-sl-ic', '⌕'))
    const input = el('input')
    input.type = 'text'
    input.placeholder = this.opts.searchPlaceholder
    input.setAttribute('aria-label', this.opts.searchPlaceholder)
    input.autocomplete = 'off'
    input.addEventListener('input', () => this.onInput())
    search.appendChild(input)
    this.input = input

    this.head = el('div', 'm-sl-head')
    this.count = el('b', 'm-sl-count', '')
    this.status = el('span', 'm-sl-status', '')
    this.head.appendChild(this.count)
    this.head.appendChild(this.status)

    const term = el('label', 'm-sl-term')
    this.termBox = el('input')
    this.termBox.type = 'checkbox'
    this.termBox.addEventListener('change', () => {
      this.draft.termOn = this.termBox.checked
      this.renderDraft()
    })
    this.termText = el('span')
    term.appendChild(this.termBox)
    term.appendChild(this.termText)
    this.termRow = term

    this.also = el('div', 'm-sl-also')
    this.rows = el('div', 'm-sl-rows')
    this.rows.setAttribute('role', 'group')
    this.more = el('div', 'm-sl-more')

    const foot = el('div', 'm-sl-foot')
    this.hint = el('span', 'm-sl-hint')
    const actions = el('span', 'm-sl-actions')
    const clear = el('button', 'm-sl-clear', 'Clear')
    clear.type = 'button'
    clear.addEventListener('click', () => this.clearDraft())
    const applyBtn = el('button', 'm-sl-apply', 'Apply')
    applyBtn.type = 'button'
    applyBtn.addEventListener('click', () => this.applyDraft())
    actions.appendChild(clear)
    actions.appendChild(applyBtn)
    foot.appendChild(this.hint)
    foot.appendChild(actions)

    const parts = [search, this.head, term, this.also, this.rows, this.more, foot]
    parts.forEach(n => pop.appendChild(n))
    pop.addEventListener('keydown', ev => this.onKey(ev))
    this.pop = pop

    this.anchor.appendChild(btn)
    this.anchor.appendChild(pop)
  }

  renderButton() {
    if (!this.btnText) return
    let text = this.disabled && this.disabledText ? this.disabledText : this.summary()
    if (this.disabled && !this.disabledText) text = this.opts.placeholder
    this.btnText.textContent = text
    this.btn.title = text
    this.btn.classList.toggle('m-sl-active', !this.disabled && !isEmptyState(this.state))
  }

  position() {
    if (this.pop.hidden) return
    const r = this.btn.getBoundingClientRect()
    const vw = document.documentElement.clientWidth
    const gutter = 16
    const width = Math.min(520, vw - (2 * gutter))
    const left = Math.max(gutter, Math.min(r.left, vw - width - gutter))
    Object.assign(this.pop.style, { top: `${Math.round(r.bottom + 6)}px`, left: `${Math.round(left)}px`, width: `${Math.round(width)}px` })
  }

  onInput() {
    const q = this.input.value
    const wasEmpty = !normalizeTerm(this.draft.q)
    this.draft.q = q
    // "All containing" switches on as soon as there is something typed
    if (wasEmpty && normalizeTerm(q) && this.opts.supportsTerms) this.draft.termOn = true
    this.renderDraft()
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetch(), 200)
  }

  onKey(ev) {
    if (ev.key === 'Escape') {
      ev.preventDefault()
      ev.stopPropagation()
      this.close({ focus: true })
      return
    }
    const boxes = [...this.rows.querySelectorAll('input[type=checkbox]')]
    const i = boxes.indexOf(ev.target)
    if (ev.key === 'Enter' && (ev.target === this.input || i !== -1 || ev.target === this.termBox)) {
      ev.preventDefault()
      this.applyDraft()
    } else if (ev.key === 'ArrowDown' && (ev.target === this.input || i !== -1)) {
      ev.preventDefault()
      const next = boxes[i + 1]
      if (next) next.focus()
    } else if (ev.key === 'ArrowUp' && i !== -1) {
      ev.preventDefault()
      if (i === 0) this.input.focus()
      else boxes[i - 1].focus()
    }
  }

  activeTerm() {
    const q = normalizeTerm(this.draft.q)
    return this.opts.supportsTerms && this.draft.termOn && q ? q : ''
  }

  draftTerms() {
    const t = this.activeTerm()
    return uniq((t ? [t] : []).concat(this.draft.otherTerms))
  }

  covered(option) {
    return this.draftTerms().some(t => matchesTerm(option.value, t) || matchesTerm(option.label, t))
  }

  toggle(option) {
    const { value } = option
    const { picks, excludes } = this.draft
    if (!this.opts.supportsExcludes && this.covered(option)) {
      this.renderDraft()
      return
    }
    if (excludes.has(value)) {
      excludes.delete(value)
      if (!this.covered(option) && picks.size) picks.add(value)
    } else if (this.covered(option)) {
      excludes.add(value)
      picks.delete(value)
    } else if (picks.has(value)) {
      picks.delete(value)
    } else {
      picks.add(value)
    }
    this.renderDraft()
  }

  clearDraft() {
    this.draft = {
      q: '', termOn: false, otherTerms: [], picks: new Set(), excludes: new Set()
    }
    this.input.value = ''
    this.renderDraft()
    this.fetch()
    this.input.focus()
  }

  applyDraft() {
    const state = {
      terms: this.draftTerms(),
      picks: [...this.draft.picks],
      excludes: [...this.draft.excludes]
    }
    this.close({ focus: true })
    this.apply(state)
  }

  draftState() {
    return cleanState({ terms: this.draftTerms(), picks: [...this.draft.picks], excludes: [...this.draft.excludes] })
  }

  remember(data) {
    (data.options || []).forEach((o) => {
      if (o.label) this.labels.set(o.value, o.label)
    })
  }

  fetch() {
    this.seq += 1
    const { seq } = this
    const q = normalizeTerm(this.draft.q)
    this.status.textContent = 'Loading…'
    this.opts.fetchOptions(q, this.draftState()).then((data) => {
      if (seq !== this.seq || !this.isOpen()) return
      this.remember(data)
      this.data = { ...data, q }
      if (q && this.opts.supportsTerms) this.termTotals.set(q, data.total_options || 0)
      this.status.textContent = ''
      this.renderDraft()
    }).catch(() => {
      if (seq !== this.seq) return
      this.status.textContent = 'Could not load the list'
    })
  }

  renderDraft() {
    const { opts, draft } = this
    const q = normalizeTerm(draft.q)
    const words = termWords(q)
    const data = this.data || { options: [], total_options: 0, total_count: 0 }

    const listed = data.options.filter(o => !o.pinned).length
    this.count.textContent = this.data
      ? `${fmt(data.total_options)} ${plural(data.total_options, opts.nounOne, opts.nounMany)} · ${fmt(data.total_count)} ${opts.countNoun}`
      : ''

    const showTerm = opts.supportsTerms && !!q
    this.termRow.hidden = !showTerm
    this.termBox.checked = !!draft.termOn
    this.termText.textContent = `All containing "${q}"`

    this.also.replaceChildren()
    this.also.hidden = !draft.otherTerms.length
    draft.otherTerms.forEach((t) => {
      const chip = el('span', 'm-sl-also-term')
      chip.appendChild(document.createTextNode(`Also containing "${t}" `))
      const x = el('button', '', '✕')
      x.type = 'button'
      x.title = `Remove "${t}"`
      x.addEventListener('click', () => {
        draft.otherTerms = draft.otherTerms.filter(o => o !== t)
        this.renderDraft()
      })
      chip.appendChild(x)
      this.also.appendChild(chip)
    })

    const top = Math.max(1, ...data.options.map(o => o.n || 0))
    this.rows.replaceChildren()
    if (this.data && !data.options.length) {
      this.rows.appendChild(el('div', 'm-sl-empty', q ? `No ${opts.nounMany} contain "${q}"` : `No ${opts.nounMany} in this range`))
    }
    data.options.forEach((o) => {
      const excluded = draft.excludes.has(o.value)
      const checked = !excluded && (draft.picks.has(o.value) || this.covered(o))
      const row = el('label', `m-sl-row${excluded ? ' off' : ''}`)
      const box = el('input')
      box.type = 'checkbox'
      box.checked = checked
      // no exclusions: a row the term covers can't be unchecked
      if (!opts.supportsExcludes && this.covered(o)) box.disabled = true
      box.addEventListener('change', () => this.toggle(o))
      row.appendChild(box)
      const name = el('span', 'm-sl-name')
      name.title = o.label
      name.appendChild(highlighted(o.label, words))
      // stored values with stray whitespace list separately (an exact pick
      // matches the spelling), so say why two rows look the same
      if (o.label !== o.label.trim()) name.appendChild(el('span', 'm-sl-note', ' (extra spaces)'))
      if (!o.in_range) name.appendChild(el('span', 'm-sl-note', ` (${opts.outOfRangeText})`))
      row.appendChild(name)
      const bar = el('span', 'm-sl-bar')
      const fill = el('i')
      fill.style.width = `${o.n ? Math.max(2, Math.round(100 * Math.sqrt(o.n / top))) : 0}%`
      bar.appendChild(fill)
      row.appendChild(bar)
      row.appendChild(el('span', 'm-sl-n', o.n ? fmt(o.n) : '—'))
      this.rows.appendChild(row)
    })
    const rest = data.total_options - listed
    this.more.hidden = !(this.data && rest > 0)
    this.more.textContent = `+ ${fmt(rest)} more (refine the search)`

    const term = this.activeTerm()
    if (term && !opts.supportsExcludes) {
      this.hint.textContent = `Every ${opts.nounOne} containing "${term}" counts, new ones too. Turn the toggle off to check single ${opts.nounMany}.`
    } else if (term) {
      this.hint.textContent = `Unchecked ${opts.nounMany} stay out. New ${opts.nounMany} containing "${term}" join automatically.`
    } else if (q && opts.supportsTerms) {
      this.hint.textContent = `Check the ${opts.nounMany} to include.`
    } else {
      this.hint.textContent = draft.picks.size ? `${draft.picks.size} picked` : ''
    }
  }
}

export function createSearchList(anchorEl, options) {
  return new SearchList(anchorEl, options)
}
